import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Loader2, Tags, Settings2, Factory, FileText, Crown, BarChart3 } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;
const ESTADOS = ["prospecto", "registro_pendiente", "aprobado", "activo", "suspendido", "inactivo"];
const BADGE = {
  registro_pendiente: "bg-yellow-100 text-yellow-800", prospecto: "bg-gray-100 text-gray-700",
  aprobado: "bg-blue-100 text-blue-800", activo: "bg-green-100 text-green-800",
  suspendido: "bg-red-100 text-red-700", inactivo: "bg-gray-200 text-gray-600",
};

export default function AdminNegociosB2B() {
  const [data, setData] = useState(null);
  const [listas, setListas] = useState([]);
  const [config, setConfig] = useState(null);
  const [consolidado, setConsolidado] = useState([]);
  const [clubCfg, setClubCfg] = useState(null);
  const [canjes, setCanjes] = useState([]);
  const [reportes, setReportes] = useState(null);
  const [aprobar, setAprobar] = useState(null);
  const [listaAprobar, setListaAprobar] = useState("tienda");
  const [cred, setCred] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const load = () => {
    api.get("/b2b/admin/negocios").then((r) => setData(r.data));
    api.get("/b2b/admin/listas").then((r) => setListas(r.data));
    api.get("/b2b/admin/config").then((r) => setConfig(r.data));
    api.get("/b2b/admin/consolidado").then((r) => setConsolidado(r.data));
    api.get("/b2b/admin/club/config").then((r) => setClubCfg(r.data));
    api.get("/b2b/admin/club/canjes").then((r) => setCanjes(r.data));
    api.get("/b2b/admin/reportes").then((r) => setReportes(r.data));
  };
  useEffect(load, []);

  const aprobarNegocio = async () => {
    setGuardando(true);
    try {
      const r = await api.post(`/b2b/admin/negocios/${aprobar.id}/aprobar`, { lista_id: listaAprobar });
      setCred(r.data);
      setAprobar(null);
      toast.success("Negocio aprobado y acceso al portal creado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error aprobando"); } finally { setGuardando(false); }
  };

  const cambiarEstado = async (n, estado) => {
    try {
      await api.put(`/b2b/admin/negocios/${n.id}`, { estado });
      toast.success(`Estado: ${estado}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const cambiarLista = async (n, lista_id) => {
    try {
      await api.put(`/b2b/admin/negocios/${n.id}`, { lista_id });
      toast.success("Lista de precios actualizada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const guardarLista = async (l) => {
    try {
      await api.put(`/b2b/admin/listas/${l.id}`, { margen_pct: l.margen_pct });
      toast.success(`Lista ${l.nombre} actualizada (nueva versión con historial)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const guardarConfig = async () => {
    setGuardando(true);
    try {
      await api.put("/b2b/admin/config", config);
      toast.success("Reglas comerciales guardadas");
    } catch (e) { toast.error("Error guardando"); } finally { setGuardando(false); }
  };

  const guardarClub = async () => {
    try {
      await api.put("/b2b/admin/club/config", clubCfg);
      toast.success("Club Negocios actualizado");
      load();
    } catch (e) { toast.error("Error guardando Club"); }
  };

  const gestionarCanje = async (c, estado) => {
    try {
      await api.put(`/b2b/admin/club/canjes/${c.id}`, { estado });
      toast.success(`Canje ${estado}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!data) return <Loader2 className="w-8 h-8 animate-spin text-torta-brown mx-auto mt-16" />;

  return (
    <div className="space-y-5" data-testid="admin-negocios-b2b-page">
      <div>
        <h1 className="font-display text-3xl text-torta-dark flex items-center gap-2"><Building2 className="w-7 h-7 text-torta-brown" /> Negocios B2B</h1>
        <p className="text-torta-muted text-sm">Establecimientos mayoristas, listas de precios y reglas comerciales</p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {ESTADOS.map((e) => (
          <div key={e} className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-torta-dark">{data.resumen[e] || 0}</p>
            <p className="text-[10px] text-torta-muted capitalize">{e.replace("_", " ")}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="negocios">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="negocios" data-testid="tab-negocios"><Building2 className="w-3.5 h-3.5 mr-1" /> Negocios</TabsTrigger>
          <TabsTrigger value="listas" data-testid="tab-listas"><Tags className="w-3.5 h-3.5 mr-1" /> Listas de precios</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><Settings2 className="w-3.5 h-3.5 mr-1" /> Reglas</TabsTrigger>
          <TabsTrigger value="produccion" data-testid="tab-produccion"><Factory className="w-3.5 h-3.5 mr-1" /> Producción</TabsTrigger>
          <TabsTrigger value="club" data-testid="tab-club-negocios"><Crown className="w-3.5 h-3.5 mr-1" /> Club Negocios</TabsTrigger>
          <TabsTrigger value="reportes" data-testid="tab-reportes-b2b"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Reportes</TabsTrigger>
        </TabsList>

        <TabsContent value="negocios" className="mt-4 space-y-3">
          {data.negocios.map((n) => (
            <div key={n.id} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`admin-negocio-${n.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-bold text-torta-dark flex items-center gap-2">
                    {n.nombre_comercial}
                    {n.requiere_factura && <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 flex items-center gap-1"><FileText className="w-3 h-3" /> Factura electrónica</span>}
                  </p>
                  <p className="text-xs text-torta-muted">{n.tipo_establecimiento} · {n.responsable} · {n.telefono} · {n.email}</p>
                  <p className="text-xs text-torta-muted">{n.direccion}, {n.barrio} {n.ciudad} {n.vendedor_referidor ? `· Referido por ${n.vendedor_referidor.nombre}` : ""}</p>
                </div>
                <Badge className={`border-0 text-[10px] ${BADGE[n.estado]}`}>{n.estado.replace("_", " ")}</Badge>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap items-center">
                {!n.usuario_id ? (
                  <Button size="sm" onClick={() => { setAprobar(n); setListaAprobar("tienda"); }} className="bg-green-600 hover:bg-green-700" data-testid={`aprobar-btn-${n.id}`}>Aprobar y activar</Button>
                ) : (
                  <select value={n.lista_id || "tienda"} onChange={(e) => cambiarLista(n, e.target.value)} className="h-8 px-2 rounded-md border text-xs bg-white" data-testid={`lista-select-${n.id}`}>
                    {listas.map((l) => <option key={l.id} value={l.id}>Lista {l.nombre} ({l.margen_pct}%)</option>)}
                  </select>
                )}
                <select value={n.estado} onChange={(e) => cambiarEstado(n, e.target.value)} className="h-8 px-2 rounded-md border text-xs bg-white" data-testid={`estado-select-${n.id}`}>
                  {ESTADOS.map((e) => <option key={e} value={e}>{e.replace("_", " ")}</option>)}
                </select>
              </div>
            </div>
          ))}
          {data.negocios.length === 0 && <p className="text-center text-sm text-torta-muted py-8">Sin negocios registrados</p>}
        </TabsContent>

        <TabsContent value="listas" className="mt-4 space-y-3">
          {listas.map((l, idx) => (
            <div key={l.id} className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 flex-wrap" data-testid={`lista-${l.id}`}>
              <div className="flex-1">
                <p className="font-bold text-torta-dark">Lista {l.nombre}</p>
                <p className="text-[11px] text-torta-muted">Versión {l.version} · {l.fecha_actualizacion?.slice(0, 10)} · margen bruto sobre precio público</p>
              </div>
              <Input type="number" className="w-20 h-9" value={l.margen_pct} onChange={(e) => { const ls = [...listas]; ls[idx] = { ...l, margen_pct: parseFloat(e.target.value) || 0 }; setListas(ls); }} />
              <span className="text-sm text-torta-muted">%</span>
              <Button size="sm" variant="outline" onClick={() => guardarLista(listas[idx])} data-testid={`guardar-lista-${l.id}`}>Guardar</Button>
            </div>
          ))}
          <p className="text-[11px] text-torta-muted">Los precios minoristas no se modifican. El precio mayorista se calcula sobre el precio público de cada producto; cada cambio guarda historial.</p>
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          {config && (
            <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3 max-w-xl">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-torta-muted mb-1">Reposición mínima ($)</p>
                  <Input type="number" value={config.minimo_valor} onChange={(e) => setConfig({ ...config, minimo_valor: parseInt(e.target.value) || 0 })} data-testid="cfg-minimo-valor" /></div>
                <div><p className="text-xs text-torta-muted mb-1">Reposición mínima (unidades)</p>
                  <Input type="number" value={config.minimo_unidades} onChange={(e) => setConfig({ ...config, minimo_unidades: parseInt(e.target.value) || 0 })} data-testid="cfg-minimo-unidades" /></div>
                <div><p className="text-xs text-torta-muted mb-1">Anticipación entrega (horas)</p>
                  <Input type="number" value={config.anticipacion_horas} onChange={(e) => setConfig({ ...config, anticipacion_horas: parseInt(e.target.value) || 0 })} /></div>
                <div><p className="text-xs text-torta-muted mb-1">Unidades recurrentes para elegible Aliado</p>
                  <Input type="number" value={config.umbral_aliado_unidades} onChange={(e) => setConfig({ ...config, umbral_aliado_unidades: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <p className="text-[11px] text-torta-muted">El primer pedido de cada negocio está exento de mínimos (Paquete Descubre la Abuela). El cambio a lista Aliado siempre requiere validación comercial manual.</p>
              <Button onClick={guardarConfig} disabled={guardando} className="bg-torta-brown hover:bg-torta-brown/90" data-testid="cfg-guardar-btn">
                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar reglas"}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="produccion" className="mt-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-sm font-bold text-torta-dark mb-2">Consolidado por producto y fecha de entrega (pedidos en curso)</p>
            {consolidado.length === 0 ? <p className="text-sm text-torta-muted py-4 text-center">Sin pedidos pendientes</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-torta-muted border-b"><th className="py-1">Fecha entrega</th><th>Producto</th><th className="text-right">Unidades</th><th className="text-right">Pedidos</th></tr></thead>
                <tbody>
                  {consolidado.map((c, i) => (
                    <tr key={i} className="border-b border-torta-sand/50">
                      <td className="py-1.5">{c.fecha_entrega}</td><td>{c.nombre}</td>
                      <td className="text-right font-bold">{c.unidades}</td><td className="text-right">{c.pedidos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
        <TabsContent value="club" className="mt-4 space-y-3">
          {clubCfg && (
            <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3 max-w-2xl">
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={clubCfg.activo} onChange={(e) => setClubCfg({ ...clubCfg, activo: e.target.checked })} /> Club activo</label>
                <div className="flex items-center gap-2 text-sm">Puntos por cada $1.000 pagado:
                  <Input type="number" className="w-16 h-8" value={clubCfg.puntos_por_mil} onChange={(e) => setClubCfg({ ...clubCfg, puntos_por_mil: parseInt(e.target.value) || 0 })} data-testid="club-puntos-mil" /></div>
              </div>
              <div>
                <p className="text-xs font-semibold text-torta-muted mb-1">Niveles (puntos históricos mínimos)</p>
                {clubCfg.niveles.map((n, i) => (
                  <div key={n.id} className="flex items-center gap-2 py-1 text-sm">
                    <span className="w-32">{n.emoji} {n.nombre}</span>
                    <Input type="number" className="w-24 h-8" value={n.puntos_min} onChange={(e) => { const ns = [...clubCfg.niveles]; ns[i] = { ...n, puntos_min: parseInt(e.target.value) || 0 }; setClubCfg({ ...clubCfg, niveles: ns }); }} />
                    <span className="text-[11px] text-torta-muted">pts</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-semibold text-torta-muted mb-1">Catálogo de recompensas</p>
                {clubCfg.recompensas.map((r, i) => (
                  <div key={r.id} className="flex items-center gap-2 py-1">
                    <Input className="flex-1 h-8" value={r.nombre} onChange={(e) => { const rs = [...clubCfg.recompensas]; rs[i] = { ...r, nombre: e.target.value }; setClubCfg({ ...clubCfg, recompensas: rs }); }} />
                    <Input type="number" className="w-20 h-8" value={r.puntos} onChange={(e) => { const rs = [...clubCfg.recompensas]; rs[i] = { ...r, puntos: parseInt(e.target.value) || 0 }; setClubCfg({ ...clubCfg, recompensas: rs }); }} />
                    <input type="checkbox" checked={r.activo} onChange={(e) => { const rs = [...clubCfg.recompensas]; rs[i] = { ...r, activo: e.target.checked }; setClubCfg({ ...clubCfg, recompensas: rs }); }} />
                  </div>
                ))}
                <Button size="sm" variant="outline" className="mt-1" onClick={() => setClubCfg({ ...clubCfg, recompensas: [...clubCfg.recompensas, { id: "", nombre: "", puntos: 100, activo: true }] })}>+ Recompensa</Button>
              </div>
              <Button onClick={guardarClub} className="bg-torta-brown hover:bg-torta-brown/90" data-testid="club-guardar-btn">Guardar Club Negocios</Button>
            </div>
          )}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-sm font-bold text-torta-dark mb-2">Canjes solicitados</p>
            {canjes.length === 0 ? <p className="text-sm text-torta-muted text-center py-3">Sin canjes</p> : canjes.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-1.5 border-b border-torta-sand/50 text-sm flex-wrap gap-1" data-testid={`canje-${c.id}`}>
                <span>{c.negocio_nombre} · <b>{c.recompensa_nombre}</b> ({c.puntos} pts)</span>
                {c.estado === "solicitado" || c.estado === "aprobado" ? (
                  <span className="flex gap-1">
                    {c.estado === "solicitado" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => gestionarCanje(c, "aprobado")}>Aprobar</Button>}
                    <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => gestionarCanje(c, "entregado")}>Entregado</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => gestionarCanje(c, "rechazado")}>Rechazar</Button>
                  </span>
                ) : <Badge className={`border-0 text-[10px] ${c.estado === "entregado" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{c.estado}</Badge>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reportes" className="mt-4">
          {reportes && (
            <div className="space-y-4" data-testid="reportes-b2b-section">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Ventas entregadas", fmt(reportes.ventas_entregadas)], ["Pedidos registrados", reportes.pedidos_registrados],
                  ["Pedidos entregados", reportes.pedidos_entregados], ["Pendientes", reportes.pedidos_pendientes],
                  ["Ticket promedio", fmt(reportes.ticket_promedio)], ["Negocios activos", reportes.negocios_activos],
                  ["Recurrentes", `${reportes.clientes_recurrentes} (${reportes.tasa_recompra}%)`], ["Cartera pendiente", fmt(reportes.cartera_pendiente)],
                  ["Puntos Club", reportes.puntos_acumulados], ["Recompensas entregadas", reportes.recompensas_entregadas],
                  ["Nuevos este mes", reportes.negocios_nuevos_mes]].map(([l, v]) => (
                  <div key={l} className="bg-white rounded-xl p-3 text-center shadow-sm"><p className="text-lg font-bold text-torta-dark">{v}</p><p className="text-[10px] text-torta-muted">{l}</p></div>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <p className="text-sm font-bold text-torta-dark mb-2">Ventas por vendedor</p>
                  {reportes.ventas_por_vendedor.map((v) => (
                    <div key={v.vendedor} className="flex justify-between text-xs py-1 border-b border-torta-sand/40"><span>{v.vendedor}</span><span>{v.pedidos} ped · <b>{fmt(v.total)}</b></span></div>
                  ))}
                  {reportes.ventas_por_vendedor.length === 0 && <p className="text-xs text-torta-muted">Sin entregas aún</p>}
                </div>
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <p className="text-sm font-bold text-torta-dark mb-2">Ventas por producto</p>
                  {reportes.ventas_por_producto.map((p) => (
                    <div key={p.nombre} className="flex justify-between text-xs py-1 border-b border-torta-sand/40"><span>{p.nombre}</span><span>{p.unidades} und · <b>{fmt(p.total)}</b></span></div>
                  ))}
                  {reportes.ventas_por_producto.length === 0 && <p className="text-xs text-torta-muted">Sin entregas aún</p>}
                </div>
              </div>
              {reportes.cartera_detalle.length > 0 && (
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <p className="text-sm font-bold text-torta-dark mb-2">Cartera pendiente</p>
                  {reportes.cartera_detalle.map((f) => (
                    <div key={f.numero} className="flex justify-between text-xs py-1 border-b border-torta-sand/40"><span>{f.numero} · {f.distribuidor_nombre}</span><b>{fmt(f.saldo)}</b></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Aprobar */}
      <Dialog open={!!aprobar} onOpenChange={(v) => !v && setAprobar(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Aprobar {aprobar?.nombre_comercial}</DialogTitle></DialogHeader>
          <p className="text-xs text-torta-muted">Se creará el acceso al portal Baruj Shefa Negocios y se enviarán las credenciales por correo a {aprobar?.email}.</p>
          <select value={listaAprobar} onChange={(e) => setListaAprobar(e.target.value)} className="w-full h-10 px-3 rounded-md border bg-white text-sm" data-testid="aprobar-lista-select">
            {listas.map((l) => <option key={l.id} value={l.id}>Lista {l.nombre} ({l.margen_pct}% margen)</option>)}
          </select>
          <Button onClick={aprobarNegocio} disabled={guardando} className="w-full bg-green-600 hover:bg-green-700" data-testid="aprobar-confirmar-btn">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aprobar y crear acceso"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Credenciales */}
      <Dialog open={!!cred} onOpenChange={(v) => !v && setCred(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Acceso creado ✅</DialogTitle></DialogHeader>
          <div className="text-sm space-y-1" data-testid="credenciales-negocio">
            <p><b>Usuario:</b> {cred?.email}</p>
            <p><b>Contraseña temporal:</b> <code className="bg-torta-cream px-2 py-0.5 rounded">{cred?.password_temporal}</code></p>
            <p className="text-xs text-torta-muted">También se enviaron por correo. Portal: {window.location.origin}/login</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
